import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListDoctors,
  adminCreateDoctor,
  adminUpdateDoctor,
  adminDeleteDoctor,
  type DoctorRow,
} from "@/lib/doctors.functions";
import { specialties, locations } from "@/lib/healic-data";
import { PageShell } from "@/components/PageShell";
import { Pencil, Trash2, Plus, LogOut, Eye, EyeOff, Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Doctor CMS — Healic Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"];

function AdminPage() {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setBootstrapping(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setIsAdmin(null);
      return;
    }
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [session?.user?.id]);

  if (bootstrapping) {
    return (
      <PageShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </PageShell>
    );
  }

  if (!session) return <LoginForm />;
  if (isAdmin === null) {
    return (
      <PageShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </PageShell>
    );
  }
  if (!isAdmin) return <NotAdmin email={session.user.email ?? ""} />;
  return <CmsDashboard userEmail={session.user.email ?? ""} />;
}

// ---------- Login ----------
function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) setError(error.message);
  };

  return (
    <PageShell>
      <div className="mx-auto flex min-h-[70vh] max-w-md items-center px-4">
        <form
          onSubmit={onSubmit}
          className="w-full rounded-3xl border border-border bg-card p-8 shadow-soft"
        >
          <h1 className="font-display text-2xl font-bold">Admin sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to manage doctors in the Healic CMS.
          </p>
          <label className="mt-6 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 block w-full rounded-pill border border-border bg-background px-4 py-2.5 text-sm normal-case tracking-normal text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Password
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 block w-full rounded-pill border border-border bg-background px-4 py-2.5 text-sm normal-case tracking-normal text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-pill bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </button>
          <p className="mt-4 text-xs text-muted-foreground">
            Admin accounts are created by the site owner. If you need access, contact the team.
          </p>
        </form>
      </div>
    </PageShell>
  );
}

function NotAdmin({ email }: { email: string }) {
  return (
    <PageShell>
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold">Access denied</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {email} is signed in but doesn't have the admin role yet.
        </p>
        <button
          onClick={() => supabase.auth.signOut()}
          className="mt-6 inline-flex items-center gap-2 rounded-pill bg-cream px-5 py-2.5 text-sm font-semibold"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </PageShell>
  );
}

// ---------- CMS ----------
type FormValues = {
  id?: string;
  slug: string;
  name: string;
  specialty: string;
  specialty_slug: string;
  experience: string;
  hospital: string;
  location_slug: string;
  gender: "male" | "female";
  qualifications: string;
  languages: string;
  about: string;
  image_url: string;
  display_order: number;
  published: boolean;
  consult_fee_inhospital: string;
  consult_fee_online: string;
  schedule_days: string;
  schedule_hours: string;
  education: string;
  work_experience: string;
  meta_title: string;
  meta_description: string;
};

const EMPTY_FORM: FormValues = {
  slug: "",
  name: "",
  specialty: "",
  specialty_slug: specialties[0]?.slug ?? "",
  experience: "",
  hospital: "",
  location_slug: "",
  gender: "male",
  qualifications: "",
  languages: "",
  about: "",
  image_url: "",
  display_order: 100,
  published: true,
  consult_fee_inhospital: "",
  consult_fee_online: "",
  schedule_days: "",
  schedule_hours: "",
  education: "",
  work_experience: "",
  meta_title: "",
  meta_description: "",
};

function fromRow(r: DoctorRow): FormValues {
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    specialty: r.specialty,
    specialty_slug: r.specialty_slug,
    experience: r.experience,
    hospital: r.hospital ?? "",
    location_slug: r.location_slug ?? "",
    gender: r.gender,
    qualifications: r.qualifications ?? "",
    languages: r.languages.join(", "),
    about: r.about ?? "",
    image_url: r.image_url ?? "",
    display_order: r.display_order,
    published: r.published,
    consult_fee_inhospital: r.consult_fee_inhospital != null ? String(r.consult_fee_inhospital) : "",
    consult_fee_online: r.consult_fee_online != null ? String(r.consult_fee_online) : "",
    schedule_days: r.schedule_days ?? "",
    schedule_hours: r.schedule_hours ?? "",
    education: (r.education ?? []).join("\n"),
    work_experience: (r.work_experience ?? []).join("\n"),
    meta_title: r.meta_title ?? "",
    meta_description: r.meta_description ?? "",
  };
}

function toPayload(f: FormValues) {
  const splitLines = (s: string) =>
    s
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);
  const parseFee = (s: string) => {
    const n = parseInt(s.trim(), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  return {
    slug: f.slug.trim(),
    name: f.name.trim(),
    specialty: f.specialty.trim(),
    specialty_slug: f.specialty_slug.trim(),
    experience: f.experience.trim(),
    hospital: f.hospital.trim() || null,
    location_slug: f.location_slug.trim() || null,
    gender: f.gender,
    qualifications: f.qualifications.trim() || null,
    languages: f.languages
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    about: f.about.trim() || null,
    image_url: f.image_url.trim() || null,
    display_order: Number(f.display_order) || 0,
    published: f.published,
    consult_fee_inhospital: parseFee(f.consult_fee_inhospital),
    consult_fee_online: parseFee(f.consult_fee_online),
    schedule_days: f.schedule_days.trim() || null,
    schedule_hours: f.schedule_hours.trim() || null,
    education: splitLines(f.education),
    work_experience: splitLines(f.work_experience),
    meta_title: f.meta_title.trim() || null,
    meta_description: f.meta_description.trim() || null,
  };
}

function CmsDashboard({ userEmail }: { userEmail: string }) {
  const list = useServerFn(adminListDoctors);
  const create = useServerFn(adminCreateDoctor);
  const update = useServerFn(adminUpdateDoctor);
  const remove = useServerFn(adminDeleteDoctor);
  const qc = useQueryClient();

  const { data: doctors = [], isLoading } = useQuery({
    queryKey: ["admin", "doctors"],
    queryFn: () => list(),
  });

  const [editing, setEditing] = useState<FormValues | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (form: FormValues) => {
      const payload = toPayload(form);
      if (form.id) {
        return update({ data: { id: form.id, values: payload } });
      }
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "doctors"] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => remove({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "doctors"] }),
  });

  return (
    <PageShell>
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">CMS</p>
            <h1 className="mt-1 font-display text-3xl font-bold">Doctors</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Signed in as {userEmail} · {doctors.length} doctor{doctors.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditing({ ...EMPTY_FORM })}
              className="inline-flex items-center gap-2 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              <Plus className="h-4 w-4" /> New doctor
            </button>
            <button
              onClick={() => supabase.auth.signOut()}
              className="inline-flex items-center gap-2 rounded-pill bg-cream px-4 py-2 text-sm font-semibold"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-card">
          {isLoading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-cream text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Specialty</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {doctors.map((d) => (
                  <tr key={d.id} className="hover:bg-cream/40">
                    <td className="px-4 py-3 font-semibold">{d.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.specialty}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {d.location_slug
                        ? (locations.find((l) => l.slug === d.location_slug)?.city ?? d.location_slug)
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{d.display_order}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold ${
                          d.published
                            ? "bg-green-100 text-green-700"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {d.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                        {d.published ? "Live" : "Hidden"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditing(fromRow(d))}
                        className="mr-2 inline-flex items-center gap-1 rounded-pill bg-cream px-3 py-1.5 text-xs font-semibold hover:bg-accent/10"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete ${d.name}? This cannot be undone.`)) {
                            deleteMutation.mutate(d.id);
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-pill bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {doctors.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      No doctors yet. Click "New doctor" to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {editing && (
        <EditorDialog
          value={editing}
          onClose={() => setEditing(null)}
          onSave={(v) => saveMutation.mutate(v)}
          saving={saveMutation.isPending}
          error={saveMutation.error instanceof Error ? saveMutation.error.message : null}
        />
      )}
    </PageShell>
  );
}

function EditorDialog({
  value,
  onClose,
  onSave,
  saving,
  error,
}: {
  value: FormValues;
  onClose: () => void;
  onSave: (v: FormValues) => void;
  saving: boolean;
  error: string | null;
}) {
  const [form, setForm] = useState<FormValues>(value);
  const set = <K extends keyof FormValues>(k: K, v: FormValues[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6">
      <div className="max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-card p-6 shadow-card sm:rounded-3xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">{form.id ? "Edit doctor" : "New doctor"}</h2>
          <button onClick={onClose} className="text-sm text-muted-foreground hover:underline">
            Cancel
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
          className="mt-6 grid gap-4 sm:grid-cols-2"
        >
          <Field label="Name *">
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="URL slug *">
            <input
              required
              pattern="[a-z0-9-]+"
              value={form.slug}
              onChange={(e) => set("slug", e.target.value)}
              className={inputCls}
              placeholder="e.g. vibha-gupta"
            />
          </Field>
          <Field label="Specialty (display) *">
            <input
              required
              value={form.specialty}
              onChange={(e) => set("specialty", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Specialty category *">
            <select
              value={form.specialty_slug}
              onChange={(e) => set("specialty_slug", e.target.value)}
              className={inputCls}
            >
              {specialties.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Location">
            <select
              value={form.location_slug}
              onChange={(e) => set("location_slug", e.target.value)}
              className={inputCls}
            >
              <option value="">— None —</option>
              {locations.map((l) => (
                <option key={l.slug} value={l.slug}>
                  {l.city}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hospital">
            <input
              value={form.hospital}
              onChange={(e) => set("hospital", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Experience">
            <input
              value={form.experience}
              onChange={(e) => set("experience", e.target.value)}
              className={inputCls}
              placeholder="e.g. 10+ years"
            />
          </Field>
          <Field label="Gender">
            <select
              value={form.gender}
              onChange={(e) => set("gender", e.target.value as "male" | "female")}
              className={inputCls}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Qualifications">
            <input
              value={form.qualifications}
              onChange={(e) => set("qualifications", e.target.value)}
              className={inputCls}
              placeholder="MBBS, MS, etc."
            />
          </Field>
          <Field label="Languages (comma-separated)">
            <input
              value={form.languages}
              onChange={(e) => set("languages", e.target.value)}
              className={inputCls}
              placeholder="English, Hindi"
            />
          </Field>
          <Field label="Photo" className="sm:col-span-2">
            <PhotoUploader
              value={form.image_url}
              onChange={(url) => set("image_url", url)}
            />
          </Field>

          <Field label="Display order">
            <input
              type="number"
              value={form.display_order}
              onChange={(e) => set("display_order", Number(e.target.value))}
              className={inputCls}
            />
          </Field>
          <Field label="About / bio" className="sm:col-span-2">
            <textarea
              value={form.about}
              onChange={(e) => set("about", e.target.value)}
              rows={4}
              className="block w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </Field>

          <Field label="Consultation fee — In-Hospital (₹)">
            <input
              type="number"
              min={0}
              value={form.consult_fee_inhospital}
              onChange={(e) => set("consult_fee_inhospital", e.target.value)}
              className={inputCls}
              placeholder="e.g. 499"
            />
          </Field>
          <Field label="Consultation fee — Online (₹)">
            <input
              type="number"
              min={0}
              value={form.consult_fee_online}
              onChange={(e) => set("consult_fee_online", e.target.value)}
              className={inputCls}
              placeholder="e.g. 399"
            />
          </Field>
          <Field label="Schedule — Days">
            <input
              value={form.schedule_days}
              onChange={(e) => set("schedule_days", e.target.value)}
              className={inputCls}
              placeholder="e.g. Mon–Sat"
            />
          </Field>
          <Field label="Schedule — Hours">
            <input
              value={form.schedule_hours}
              onChange={(e) => set("schedule_hours", e.target.value)}
              className={inputCls}
              placeholder="e.g. 9:00 AM – 6:00 PM"
            />
          </Field>
          <Field label="Education & Training (one entry per line)" className="sm:col-span-2">
            <textarea
              value={form.education}
              onChange={(e) => set("education", e.target.value)}
              rows={4}
              className="block w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              placeholder={"MBBS — AIIMS Delhi\nMS Orthopaedics — PGI Chandigarh\nFellowship in Joint Replacement"}
            />
          </Field>
          <Field label="Work Experience (one entry per line)" className="sm:col-span-2">
            <textarea
              value={form.work_experience}
              onChange={(e) => set("work_experience", e.target.value)}
              rows={4}
              className="block w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              placeholder={"Consultant — Healic Hospitals (2020 – Present)\nSenior Resident — AIIMS (2016 – 2020)"}
            />
          </Field>
          <Field label="SEO — Meta title (optional, ~60 chars)" className="sm:col-span-2">
            <input
              value={form.meta_title}
              onChange={(e) => set("meta_title", e.target.value)}
              className={inputCls}
              placeholder="Leave blank to auto-generate"
              maxLength={160}
            />
          </Field>
          <Field label="SEO — Meta description (optional, ~160 chars)" className="sm:col-span-2">
            <textarea
              value={form.meta_description}
              onChange={(e) => set("meta_description", e.target.value)}
              rows={3}
              className="block w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              placeholder="Leave blank to auto-generate"
              maxLength={320}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
            />
            Published (visible on the public site)
          </label>

          {error && <p className="text-sm text-red-600 sm:col-span-2">{error}</p>}

          <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-pill bg-cream px-5 py-2.5 text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {form.id ? "Save changes" : "Create doctor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "block w-full rounded-pill border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block text-xs font-semibold uppercase tracking-wide text-muted-foreground ${className ?? ""}`}>
      {label}
      <div className="mt-1.5 normal-case tracking-normal text-foreground">{children}</div>
    </label>
  );
}

function PhotoUploader({
  value,
  onChange,
}: {
  value: string;
  onChange: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setErr(null);
    if (!file.type.startsWith("image/")) {
      setErr("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `doctors/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("doctor-photos")
        .upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
      if (error) throw error;
      const { data } = supabase.storage.from("doctor-photos").getPublicUrl(path);
      onChange(data.publicUrl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {value ? (
          <img
            src={value}
            alt="Doctor"
            className="h-20 w-20 rounded-2xl border border-border object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-border bg-cream text-xs text-muted-foreground">
            No photo
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-pill bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">
            {uploading && <Loader2 className="h-3 w-3 animate-spin" />}
            {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = "";
              }}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-muted-foreground hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className={inputCls}
      />
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

